<script>
	import { normalizeCollection, playerUrl, teamUrl } from './shared.js';

	export let plays = [];
	export let teams = [];
	export let team1 = null;
	export let team2 = null;
	export let tag = 'mlbl';
	export let compId = '';

	const BASE_PLAY_EXCLUDED = [24, 69, 70, 71, 72, 73];

	const PLAY_DESCRIPTIONS = {
		0: '—',
		1: '1 очко',
		2: '2 очка',
		3: '3 очка',
		4: 'Мимо 1 очко',
		5: 'Мимо 2 очка',
		6: 'Мимо 3 очка',
		7: 'Замена',
		8: 'Выход',
		9: 'Уход',
		10: 'Другая потеря',
		11: 'Пас-потеря',
		12: 'Пробежка',
		13: 'Заступ/аут',
		14: 'Зона',
		15: 'Неправильное ведение',
		16: '3 секунды',
		17: '5 секунд',
		18: '8 секунд',
		19: '24 секунды',
		20: 'Потеря мяча',
		21: 'Начало периода',
		22: 'Конец периода',
		23: 'Таймаут',
		24: 'Владение',
		25: 'Передача',
		26: 'Перехват',
		27: 'Блокшот',
		28: 'Подбор',
		29: 'Быстрый отрыв',
		30: 'Слэм-данк',
		31: 'Бросок в прыжке',
		32: 'Лей-ап',
		33: 'Добивание',
		34: 'Аллей-уп',
		35: 'Крюком',
		36: 'Штрафной',
		40: 'Фол P',
		41: 'Фол U',
		42: 'Фол T',
		43: 'Фол D',
		44: 'Фол C',
		45: 'Фол B',
		46: 'Фол F',
		47: 'Фол в нападении',
		50: 'Вбрасывание',
		51: '1 бросок',
		52: '2 броска',
		53: '3 броска',
		54: 'Компенсация',
		60: 'Разминка',
		61: 'Представление команд',
		62: 'Матч скоро начнется',
		63: 'Матч вот-вот начнется',
		64: 'Матч задерживается',
		65: 'Матч прерван',
		66: 'Матч возобновлен',
		67: 'Нахватка игроков',
		68: 'Лишение права',
		69: 'Корректировка',
		100: 'Матч завершен'
	};

	$: startMap = buildStartMap(teams);
	$: rows = buildRows(plays, startMap);

	function buildStartMap(teamList) {
		const map = {};
		for (const team of teamList) {
			for (const start of normalizeCollection(team?.starts)) {
				if (start?.startNum !== null && start?.startNum !== undefined) {
					map[start.startNum] = start;
				}
			}
		}
		return map;
	}

	function getPlayDescription(playType) {
		if (playType === null || playType === undefined) {
			return '—';
		}
		return PLAY_DESCRIPTIONS[Number(playType)] ?? String(playType);
	}

	function isBasePlay(playType) {
		return !BASE_PLAY_EXCLUDED.includes(Number(playType));
	}

	function formatPlayTime(sec) {
		if (sec === null || sec === undefined) {
			return '—';
		}
		const totalSeconds = Math.floor(Number(sec) / 10);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
	}

	function getPeriodLabel(period) {
		const p = Number(period);
		if (p >= 1 && p <= 4) {
			return `${p} четверть`;
		}
		if (p > 4) {
			return 'Овертайм';
		}
		return 'Период';
	}

	function buildChip(start, teamNumber, showPlayer) {
		if (!showPlayer) {
			return null;
		}
		if (start && start.startRole !== 'Team' && start.startRole !== 'RefereeTeam') {
			return { kind: 'player', start };
		}
		if (teamNumber === 1 || teamNumber === 2) {
			return { kind: 'team', teamNumber };
		}
		return null;
	}

	function buildRows(playList, map) {
		const base = normalizeCollection(playList)
			.filter((play) => isBasePlay(play?.play))
			.sort((a, b) => (a?.num ?? 0) - (b?.num ?? 0));

		let score1 = 0;
		let score2 = 0;
		const result = [];

		for (const play of base) {
			const parentStart = map[play?.startNum];
			const teamNumber = parentStart?.teamNumber ?? 0;
			const points = play?.play === 1 ? 1 : play?.play === 2 ? 2 : play?.play === 3 ? 3 : 0;

			if (points > 0) {
				if (teamNumber === 1) {
					score1 += points;
				} else if (teamNumber === 2) {
					score2 += points;
				}
			}

			const children = normalizeCollection(play?.children)
				.sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0))
				.map((child) => {
					const childStart = map[child?.startNum];
					const childTeam = childStart?.teamNumber ?? 0;
					return {
						desc: getPlayDescription(child?.play),
						chip: buildChip(childStart, childTeam, child?.startNum !== play?.startNum),
						teamNumber: childTeam
					};
				});

			const baseItem = {
				desc: getPlayDescription(play?.play),
				chip: buildChip(parentStart, teamNumber, true),
				points: points > 0 ? points : 0
			};

			let left = [];
			let right = [];

			if (teamNumber === 1) {
				left = [baseItem, ...children];
			} else if (teamNumber === 2) {
				right = [baseItem, ...children];
			} else {
				left = children.filter((child) => child.teamNumber === 1);
				right = children.filter((child) => child.teamNumber === 2);
			}

			result.push({
				num: play?.num,
				period: play?.period,
				sec: play?.sec,
				desc: getPlayDescription(play?.play),
				score1,
				score2,
				scoreChanged: points > 0,
				left,
				right
			});
		}

		result.reverse();
		let prevPeriod = null;
		for (const row of result) {
			if (row.period !== prevPeriod) {
				row.showPeriod = true;
				row.periodLabel = getPeriodLabel(row.period);
				prevPeriod = row.period;
			}
		}

		return result;
	}

	function getPlayerHref(start) {
		if (!start?.personId) {
			return '';
		}
		return playerUrl(start.personId, { tag, compId });
	}

	function getTeamHref(teamNumber) {
		const team = teamNumber === 1 ? team1 : team2;
		const id = team?.teamId || team?.id;
		if (!id) {
			return '';
		}
		return teamUrl(id, { tag, compId });
	}

	function getPlayerTitle(start) {
		return [start?.lastName, start?.firstName, start?.secondName].filter(Boolean).join(' ') || '—';
	}

	function getPlayerNumber(start) {
		return start?.displayNumber || start?.playerNumber || '';
	}

	function getPlayerInitials(start, fallback = 'P') {
		const first = start?.firstName?.[0] || '';
		const last = start?.lastName?.[0] || '';
		return `${first}${last}`.trim() || fallback;
	}

	function getTeamTitle(teamNumber) {
		const team = teamNumber === 1 ? team1 : team2;
		return team?.name || team?.shortName || team?.abcName || `Команда ${teamNumber}`;
	}

	function getTeamInitials(teamNumber) {
		const team = teamNumber === 1 ? team1 : team2;
		return (team?.shortName?.[0] || team?.abcName?.[0] || team?.name?.[0] || 'T').toUpperCase();
	}
</script>

<div class="pbp">
	{#if rows.length}
		{#each rows as row}
			{#if row.showPeriod}
				<div class="pbp__period">{row.periodLabel}</div>
			{/if}
			<div class="pbp__row">
				<div class="pbp__side pbp__side--left">
					{#each row.left as item}
						<div class="pbp__item">
							<span class="pbp__badge">{item.desc}</span>
							{#if item.chip}
								{#if item.chip.kind === 'player'}
									{#if getPlayerHref(item.chip.start)}
										<a class="pbp__chip" href={getPlayerHref(item.chip.start)}>
											{#if item.chip.start.photo}
												<img class="pbp__chip-photo" src={item.chip.start.photo} alt={getPlayerTitle(item.chip.start)}>
											{:else}
												<span class="pbp__chip-photo pbp__chip-photo--placeholder">{getPlayerInitials(item.chip.start)}</span>
											{/if}
											<span class="pbp__chip-name"><small>#{getPlayerNumber(item.chip.start)}</small> {getPlayerTitle(item.chip.start)}</span>
										</a>
									{:else}
										<span class="pbp__chip pbp__chip--static">
											{#if item.chip.start.photo}
												<img class="pbp__chip-photo" src={item.chip.start.photo} alt={getPlayerTitle(item.chip.start)}>
											{:else}
												<span class="pbp__chip-photo pbp__chip-photo--placeholder">{getPlayerInitials(item.chip.start)}</span>
											{/if}
											<span class="pbp__chip-name"><small>#{getPlayerNumber(item.chip.start)}</small> {getPlayerTitle(item.chip.start)}</span>
										</span>
									{/if}
								{:else}
									{#if getTeamHref(item.chip.teamNumber)}
										<a class="pbp__chip" href={getTeamHref(item.chip.teamNumber)}>
											<span class="pbp__chip-photo pbp__chip-photo--placeholder">{getTeamInitials(item.chip.teamNumber)}</span>
											<span class="pbp__chip-name">{getTeamTitle(item.chip.teamNumber)}</span>
										</a>
									{:else}
										<span class="pbp__chip pbp__chip--static">
											<span class="pbp__chip-photo pbp__chip-photo--placeholder">{getTeamInitials(item.chip.teamNumber)}</span>
											<span class="pbp__chip-name">{getTeamTitle(item.chip.teamNumber)}</span>
										</span>
									{/if}
								{/if}
							{/if}
							{#if item.points}
								<span class="pbp__points">+{item.points}</span>
							{/if}
						</div>
					{/each}
				</div>

				<div class="pbp__center">
					<span class="pbp__label">{row.desc}</span>
					<span class="pbp__time">{formatPlayTime(row.sec)}</span>
					{#if row.scoreChanged}
						<div class="pbp__score">
							<span>{row.score1}</span>
							<span class="pbp__score-sep">:</span>
							<span>{row.score2}</span>
						</div>
					{/if}
				</div>

				<div class="pbp__side pbp__side--right">
					{#each row.right as item}
						<div class="pbp__item">
							<span class="pbp__badge">{item.desc}</span>
							{#if item.chip}
								{#if item.chip.kind === 'player'}
									{#if getPlayerHref(item.chip.start)}
										<a class="pbp__chip" href={getPlayerHref(item.chip.start)}>
											{#if item.chip.start.photo}
												<img class="pbp__chip-photo" src={item.chip.start.photo} alt={getPlayerTitle(item.chip.start)}>
											{:else}
												<span class="pbp__chip-photo pbp__chip-photo--placeholder">{getPlayerInitials(item.chip.start)}</span>
											{/if}
											<span class="pbp__chip-name"><small>#{getPlayerNumber(item.chip.start)}</small> {getPlayerTitle(item.chip.start)}</span>
										</a>
									{:else}
										<span class="pbp__chip pbp__chip--static">
											{#if item.chip.start.photo}
												<img class="pbp__chip-photo" src={item.chip.start.photo} alt={getPlayerTitle(item.chip.start)}>
											{:else}
												<span class="pbp__chip-photo pbp__chip-photo--placeholder">{getPlayerInitials(item.chip.start)}</span>
											{/if}
											<span class="pbp__chip-name"><small>#{getPlayerNumber(item.chip.start)}</small> {getPlayerTitle(item.chip.start)}</span>
										</span>
									{/if}
								{:else}
									{#if getTeamHref(item.chip.teamNumber)}
										<a class="pbp__chip" href={getTeamHref(item.chip.teamNumber)}>
											<span class="pbp__chip-photo pbp__chip-photo--placeholder">{getTeamInitials(item.chip.teamNumber)}</span>
											<span class="pbp__chip-name">{getTeamTitle(item.chip.teamNumber)}</span>
										</a>
									{:else}
										<span class="pbp__chip pbp__chip--static">
											<span class="pbp__chip-photo pbp__chip-photo--placeholder">{getTeamInitials(item.chip.teamNumber)}</span>
											<span class="pbp__chip-name">{getTeamTitle(item.chip.teamNumber)}</span>
										</span>
									{/if}
								{/if}
							{/if}
							{#if item.points}
								<span class="pbp__points">+{item.points}</span>
							{/if}
						</div>
					{/each}
				</div>
			</div>
		{/each}
	{:else}
		<div class="pbp__empty">Ход игры не найден</div>
	{/if}
</div>

<style>
	:host { display: block; }
	.pbp {
		display: flex;
		flex-direction: column;
	}
	.pbp__row {
		display: grid;
		grid-template-columns: 1fr 120px 1fr;
		align-items: center;
		gap: 12px;
		padding: 10px 0;
		border-bottom: 1px solid #eef1f6;
	}
	.pbp__row:last-child {
		border-bottom: 0;
	}
	.pbp__side {
		display: flex;
		flex-direction: column;
		gap: 6px;
		min-width: 0;
	}
	.pbp__side--left {
		align-items: flex-end;
		text-align: right;
	}
	.pbp__side--right {
		align-items: flex-start;
		text-align: left;
	}
	.pbp__item {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}
	.pbp__side--left .pbp__item {
		align-items: flex-end;
	}
	.pbp__side--right .pbp__item {
		align-items: flex-start;
	}
	.pbp__badge {
		font-size: 11px;
		line-height: 1;
		color: #6b7a90;
	}
	.pbp__chip {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 3px 10px 3px 4px;
		border-radius: 999px;
		background: #f2f5f9;
		text-decoration: none;
		color: #122033;
		max-width: 100%;
	}
	a.pbp__chip:hover {
		background: #e6ecf4;
	}
	.pbp__chip--static {
		cursor: default;
	}
	.pbp__chip-photo {
		width: 32px;
		height: 32px;
		border-radius: 999px;
		object-fit: cover;
		background: #e6ecf4;
		flex: 0 0 32px;
	}
	.pbp__chip-photo--placeholder {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		font-weight: 700;
		color: #00244d;
	}
	.pbp__chip-name {
		font-size: 13px;
		font-weight: 600;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.pbp__chip-name small {
		opacity: 0.5;
		font-weight: 700;
	}
	.pbp__points {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 26px;
		height: 26px;
		padding: 0 8px;
		border-radius: 999px;
		background: #22a55b;
		color: #fff;
		font-size: 12px;
		font-weight: 700;
	}
	.pbp__center {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		align-self: start;
		padding-top: 2px;
	}
	.pbp__label {
		font-size: 12px;
		font-weight: 600;
		color: #6b7a90;
		text-align: center;
	}
	.pbp__time {
		font-size: 13px;
		font-weight: 600;
		color: #53657b;
	}
	.pbp__score {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 3px 12px;
		border-radius: 10px;
		background: #fff;
		border: 1px solid #e3e8ef;
		font-size: 18px;
		font-weight: 800;
		color: #122033;
	}
	.pbp__score-sep {
		color: #6b7a90;
	}
	.pbp__empty {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 88px;
		color: #53657b;
		text-align: center;
	}
	.pbp__period {
		padding: 14px 0 6px;
		font-size: 12px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: #00244d;
		text-align: center;
	}

	@media (max-width: 640px) {
		.pbp__row {
			grid-template-columns: 1fr 88px 1fr;
			gap: 8px;
		}
		.pbp__chip-name {
			font-size: 12px;
		}
	}
</style>
